/**
 * JSON-LD structured data for SEO (https://schema.org/).
 * Used by guide pages so search engines get rich Article + Breadcrumb data.
 */

const SITE_URL = "https://biedradar.nl";
const SITE_NAME = "Biedradar";
const DEFAULT_AUTHOR = "Biedradar Redactie";

interface ArticleInput {
  title: string;
  description: string;
  path: string; // e.g. "/blog/hoeveel-overbieden-nederland-2026"
  publishedAt?: string;
  updatedAt?: string;
  readingTime?: number;
  wordCount?: number;
  /** Named human author. Falls back to the org byline when omitted. */
  author?: { name: string; url?: string };
}

export function generateArticleSchema(a: ArticleInput) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.description,
    image: `${SITE_URL}/og-image.png`,
    author: a.author
      ? {
          "@type": "Person",
          name: a.author.name,
          ...(a.author.url && { url: a.author.url }),
        }
      : { "@type": "Organization", name: DEFAULT_AUTHOR },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.svg` },
    },
    ...(a.publishedAt && { datePublished: a.publishedAt }),
    dateModified: a.updatedAt || a.publishedAt,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${a.path}` },
    ...(a.wordCount && { wordCount: a.wordCount }),
    ...(a.readingTime && { timeRequired: `PT${a.readingTime}M` }),
  };
}

export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.url}`,
    })),
  };
}

export function generateFAQSchema(faq: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

// ItemList schema for ranked listicle pages (e.g. "beste buurten in {stad}").
// Each item is a positioned ListItem; search engines can render these as a
// ranked list. Pass items already in rank order.
export function generateItemListSchema(
  name: string,
  items: Array<{ name: string; description?: string }>,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    numberOfItems: items.length,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.description && { description: item.description }),
    })),
  };
}

/**
 * Person + ProfilePage schema for an author profile page. Strengthens E-E-A-T
 * signals (Google + answer engines) by tying every article to a real,
 * resolvable person who works for the publisher.
 */
export function generatePersonSchema(p: {
  name: string;
  jobTitle?: string;
  description?: string;
  path: string; // e.g. "/auteur/marijn-alfred"
}) {
  const url = `${SITE_URL}${p.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: p.name,
      ...(p.jobTitle && { jobTitle: p.jobTitle }),
      ...(p.description && { description: p.description }),
      url,
      worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    },
  };
}

/** Merge multiple schema objects into one @graph block. */
export function combineSchemas(...schemas: object[]) {
  return { "@context": "https://schema.org", "@graph": schemas };
}
